import type { EntityTypeDto, SaveEntityTypeRequestDto } from '@gr/core/content/entities/dto';
import { fieldKeyFromLabel } from '@gr/core/content/entities/field-key';
import { FieldType, ReferenceTargetKind, type FieldDefinition } from '@gr/core/content/entities/fields';

/**
 * Строка конструктора полей. rowId — постоянный идентификатор строки в форме: на него ссылается
 * поле-заголовок, он не меняется при пересчёте ключа из подписи (SYS-UC-12.CC-1).
 * existing — поле сохранено на сервере: его тип и ключ не меняются (ENT-UC-2.CC-1, ENT-RULE-2).
 */
export interface FieldRow extends FieldDefinition {
  rowId: string;
  existing: boolean;
}

/** Значения формы конструктора: справочник (ENT-UC-1, ENT-UC-2) или поля постов (ENT-UC-4, без названий и заголовка). */
export interface FieldsFormValues {
  name: string;
  pluralName: string;
  /** rowId строки-заголовка; пусто — у полей постов заголовка нет. */
  titleRowId: string;
  fields: FieldRow[];
}

export const ENTITY_TYPE_FORM_FIELDS = ['name', 'pluralName', 'titleFieldKey', 'fields'] as const;

export function toRows(fields: readonly FieldDefinition[]): FieldRow[] {
  return fields.map((field) => ({ ...field, rowId: `saved:${field.key}`, existing: true }));
}

export function fromRows(rows: readonly FieldRow[]): FieldDefinition[] {
  return rows.map(({ rowId: _rowId, existing: _existing, ...field }) => field);
}

/** Начальные значения: новый справочник получает текстовое поле-заголовок; у сохранённого — его поля. */
export function entityTypeFormValues(type: EntityTypeDto | null, defaultTitleLabel: string): FieldsFormValues {
  if (!type) {
    const title: FieldRow = { key: 'name', label: defaultTitleLabel, type: FieldType.Text, required: true, rowId: 'new:title', existing: false };
    return { name: '', pluralName: '', titleRowId: title.rowId, fields: [title] };
  }
  const fields = toRows(type.fields);
  return { name: type.name, pluralName: type.pluralName, titleRowId: fields.find((row) => row.key === type.titleFieldKey)?.rowId ?? '', fields };
}

export function postFieldsFormValues(fields: readonly FieldDefinition[]): FieldsFormValues {
  return { name: '', pluralName: '', titleRowId: '', fields: toRows(fields) };
}

/** Текстовое поле, на которое указывает заголовок; если его нет — первое текстовое (SYS-UC-12.CC-1). */
export function titleRowOf(values: Pick<FieldsFormValues, 'titleRowId' | 'fields'>): FieldRow | null {
  const textRows = values.fields.filter((row) => row.type === FieldType.Text);
  return textRows.find((row) => row.rowId === values.titleRowId) ?? textRows[0] ?? null;
}

/** Запрос сохранения справочника: заголовок — ключ строки-заголовка на момент отправки. */
export function toSaveRequest(values: FieldsFormValues): SaveEntityTypeRequestDto {
  return { name: values.name, pluralName: values.pluralName || values.name, titleFieldKey: titleRowOf(values)?.key ?? '', fields: fromRows(values.fields) };
}

/** Ключ новой строки по подписи (ENT-RULE-2); у сохранённого поля ключ не меняется — null. */
export function keyForLabel(row: FieldRow, label: string, takenKeys: readonly string[]): string | null {
  return row.existing ? null : fieldKeyFromLabel(label || 'field', takenKeys);
}

/** Пустая строка нового поля с уникальным ключом. */
export function newFieldRow(rowId: string, takenKeys: readonly string[]): FieldRow {
  return { key: fieldKeyFromLabel('field', takenKeys), label: '', type: FieldType.Text, required: false, rowId, existing: false };
}

/** Свойства, зависящие от типа поля: варианты — у выбора, цель — у ссылки, «несколько» — у обоих. */
export function retype(row: FieldRow, type: FieldType): Pick<FieldRow, 'options' | 'target' | 'multiple'> {
  return {
    options: type === FieldType.Select ? (row.options ?? []) : undefined,
    target: type === FieldType.Reference ? (row.target ?? { kind: ReferenceTargetKind.Post }) : undefined,
    multiple: type === FieldType.Select || type === FieldType.Reference ? row.multiple : undefined,
  };
}

/** Каким должно стать поле-заголовок после удаления строки или смены её типа (SYS-UC-12.CC-1). */
export function reconcileTitleRowId(values: Pick<FieldsFormValues, 'titleRowId' | 'fields'>): string {
  return titleRowOf(values)?.rowId ?? '';
}
